import React, { useCallback } from 'react'
import { createPortal } from 'react-dom'
import { twMerge as twm } from 'tailwind-merge'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { usePageTitle } from '../../hooks/usePageTitle'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { Icon } from '../Icon'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import { CategoryLiteModal } from '../onboarding/CategoryLiteModal'
import { ItemLiteModal } from '../onboarding/ItemLiteModal'
import { RecordLiteModal } from '../onboarding/RecordLiteModal'
import { ArrangeableCustomOptions } from './ArrangeableCustomOptions'
import { BookingNotificationsSection } from './BookingNotificationsSection'
import { DiscountModal } from './DiscountModal'
import { EditExpenseLineModal } from './EditExpenseLineModal'
import { ExpenseLine } from './ExpenseLine'
import { FormAndContractCard } from './FormAndContractCard'
import { ItemLine } from './ItemLine'
import { NotificationsSection } from './NotificationsSection'
import { PhotoPicker } from './PhotoPicker'
import { Receipt, ReceiptView } from './ReceiptView'
import { RemovableItemContainer } from './RemovableItemContainer'
import { ResetContainer, useResetContainerContext } from './ResetContainer'
import { SalesTaxModal } from './SalesTaxModal'
import { SelectedItem } from './SelectedItem'
import { SuccessView } from './SuccessView'
import { WorkflowIconSection } from './WorkflowIconSection'
import { WorkflowPageCard } from './WorkflowPageCard'
import { useNavigationHandlers } from './useNavigationHandlers'

type WorkflowPageProps = React.ComponentPropsWithoutRef<'div'> & {
    title?: string
    backButtonTitle?: string
    onBack?: () => void
    // If using the second type, the onClose button will go back to the last page
    // in the history stack that does not match excludePathname. If none exist, it
    // will go to the defaultPathname.
    onClose?:
        | (() => void)
        | { action: 'go-back'; excludePathname: RegExp; defaultPathname: string }
        | null
    footerContent?: React.ReactNode
    steps?: string[]
    currentStepIndex?: number
    shouldForceCurrentStepCheckmark?: boolean
    shouldVerticallyCenterContent?: boolean
    isCloseDisabled?: boolean
}

const WorkflowPage = ({
    children,
    title,
    backButtonTitle,
    onBack,
    steps,
    currentStepIndex,
    shouldForceCurrentStepCheckmark = false,
    onClose,
    footerContent,
    shouldVerticallyCenterContent,
    isCloseDisabled = false,
    ...props
}: WorkflowPageProps) => {
    const navigate = usePSNavigate()
    const { findLastHistoryEntry } = useHistoryTrackerContext()

    usePageTitle(title)

    const handleClose = useCallback(() => {
        if (onClose === null) {
            return
        }
        if (onClose === undefined) {
            navigate(-1)
            return
        }

        if (typeof onClose === 'function') {
            onClose()
        }
        else {
            const lastEntry = findLastHistoryEntry({ excluding: onClose.excludePathname })
            if (lastEntry !== undefined) {
                lastEntry.goBackTo()
            }
            else {
                navigate(onClose.defaultPathname)
            }
        }
    }, [onClose, navigate, findLastHistoryEntry])

    return createPortal(
        <div
            className="fixed inset-0 flex h-screen w-full flex-col bg-gray-100"
            data-testid="page-container"
            {...props}
        >
            <div
                className="workflow-page-content-container flex flex-1 flex-col items-stretch justify-start overflow-y-auto p-8 pt-0"
                data-testid="content-container"
            >
                <div className="grid grid-cols-3 grid-rows-1 pt-8 pr-1">
                    <div>
                        {backButtonTitle !== undefined ? (
                            <Button
                                intent="tertiaryLink"
                                leftIcon="chevronLeft.medium"
                                onClick={onBack}
                                className="px-0"
                                isDense
                            >
                                <Label className="cursor-pointer">{backButtonTitle}</Label>
                            </Button>
                        ) : null}
                    </div>

                    {steps !== undefined && currentStepIndex !== undefined ? (
                        <div className="flex items-center justify-center">
                            <div
                                className="grid h-12 grid-rows-1"
                                style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}
                            >
                                {steps.map((stepName, stepIndex) => (
                                    <div
                                        key={stepIndex}
                                        className="grid grid-cols-[1fr_min-content_1fr] grid-rows-2"
                                    >
                                        <div
                                            className={twm(
                                                'flex items-center justify-center',
                                                stepIndex === 0 && 'opacity-0'
                                            )}
                                        >
                                            <div className="mr-2 h-0.5 w-full bg-gray-300" />
                                        </div>
                                        <div
                                            className={twm(
                                                'flex size-5 items-center justify-center rounded-full border-2',
                                                stepIndex <= currentStepIndex
                                                    ? 'border-primary-700'
                                                    : 'border-gray-300',
                                                (stepIndex < currentStepIndex ||
                                                    (stepIndex === currentStepIndex &&
                                                        shouldForceCurrentStepCheckmark)) &&
                                                    'bg-primary-700'
                                            )}
                                        >
                                            {stepIndex < currentStepIndex ||
                                            (stepIndex === currentStepIndex &&
                                                shouldForceCurrentStepCheckmark) ? (
                                                <Icon
                                                    icon="checkmark.small"
                                                    className="size-3 text-white"
                                                />
                                            ) : stepIndex === currentStepIndex ? (
                                                <div className="bg-primary-700 size-[10px] rounded-full" />
                                            ) : null}
                                        </div>
                                        <div
                                            className={twm(
                                                'flex items-center justify-center',
                                                stepIndex === steps.length - 1 && 'opacity-0'
                                            )}
                                        >
                                            <div className="ml-2 h-0.5 w-full bg-gray-300" />
                                        </div>

                                        <Label
                                            className={twm(
                                                'col-span-3 px-4 text-center',
                                                stepIndex === currentStepIndex
                                                    ? 'text-gray-700'
                                                    : 'text-gray-400'
                                            )}
                                        >
                                            {stepName}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div />
                    )}

                    {onClose !== null && (
                        <div className="relative">
                            <Button
                                shape="square"
                                intent="tertiary_deprecated"
                                disabled={isCloseDisabled}
                                onClick={handleClose}
                                className="absolute top-[-14px] right-[-18px] px-0"
                            >
                                <Icon icon="close.large" />
                            </Button>
                        </div>
                    )}
                </div>
                <main
                    className={twm(
                        'max-w-xlarge mx-auto flex w-full grow flex-col items-center',
                        shouldVerticallyCenterContent ? 'justify-center' : 'justify-start'
                    )}
                >
                    {title && (
                        <div className="flex items-center justify-center">
                            <Heading
                                size="xl"
                                className={twm(
                                    'max-w-[580px] text-center',
                                    steps !== undefined
                                        ? 'mt-12'
                                        : backButtonTitle !== undefined
                                          ? 'mt-[5px]'
                                          : 'mt-8'
                                )}
                            >
                                {title}
                            </Heading>
                        </div>
                    )}

                    {children}
                </main>
            </div>
            {footerContent !== undefined && (
                <footer className="z-10 w-full bg-white px-8 py-4 shadow-[0px_-1px_2px_0px_rgba(0,0,0,0.1)]">
                    {footerContent}
                </footer>
            )}
        </div>,
        document.body
    )
}

WorkflowPage.IconSection = WorkflowIconSection
WorkflowPage.RemovableItemContainer = RemovableItemContainer
WorkflowPage.SelectedItem = SelectedItem
WorkflowPage.ReceiptView = ReceiptView
WorkflowPage.SuccessView = SuccessView
WorkflowPage.ResetContainer = ResetContainer
WorkflowPage.useResetContainerContext = useResetContainerContext
WorkflowPage.Card = WorkflowPageCard
WorkflowPage.DiscountModal = DiscountModal
WorkflowPage.SalesTaxModal = SalesTaxModal
WorkflowPage.NavigationBlocker = NavigationBlockerPrompt
WorkflowPage.useNavigationHandlers = useNavigationHandlers
WorkflowPage.PhotoPicker = PhotoPicker
WorkflowPage.ItemLiteModal = ItemLiteModal
WorkflowPage.CategoryLiteModal = CategoryLiteModal
WorkflowPage.RecordLiteModal = RecordLiteModal
WorkflowPage.NotificationsSection = NotificationsSection
WorkflowPage.BookingNotificationsSection = BookingNotificationsSection
WorkflowPage.ExpenseLine = ExpenseLine
WorkflowPage.EditExpenseLineModal = EditExpenseLineModal
WorkflowPage.ItemLine = ItemLine
WorkflowPage.FormAndContractCard = FormAndContractCard
WorkflowPage.ArrangeableCustomOptions = ArrangeableCustomOptions

export type { Receipt }

export { WorkflowPage }
